import { z } from "zod";

export const superUserCategory = z.enum([
  "read",
  "write",
  "licensing",
  "responses",
  "accounts",
  "admin",
]);

const superuserSchema = z.object({
  read: z.boolean(),
  write: z.boolean(),
  licensing: z.boolean(),
  responses: z.boolean(),
  accounts: z.boolean(),
  admin: z.boolean(),
});

export const userApiSchema = z.object({
  userId: z.string(),
  emailAddress: z.string().nullable(),
  displayName: z.string(),
  avatarAssetId: z.string().nullable(),
  emailVerified: z.boolean(),
  languageCodePreference: z.string(),
  timezonePreference: z.string(),
  otpEnabled: z.boolean(),
  creationTime: z.string(),
  lastActiveTime: z.string(),
  googleId: z.string().nullable(),
  azureId: z.string().nullable(),
  facebookId: z.string().nullable(),
  hasSuomiFiAuth: z.boolean(),
  homeTownName: z.string().nullable(),
  homeTownNumber: z.number().int().nullable(),
  suomiFiGroupIdList: z.array(z.string()),
  hasPassword: z.boolean(),
  searchVisibility: z.boolean(),
  marketingPreference: z.boolean(),
  disabled: z.boolean(),
  superuser: superuserSchema.nullable().optional(),
});

export type User = z.infer<typeof userApiSchema>;

const groupMembershipSchema = z.object({
  groupId: z.string(),
  groupName: z.string(),
  groupDescription: z.string(),
  groupAvatarAssetId: z.string().nullable(),
  groupDisabled: z.boolean(),
  memberTitle: z.string(),
  requestedTime: z.string(),
  confirmationTime: z.string(),
  userAccepted: z.boolean(),
  groupAccepted: z.boolean(),
  userRequested: z.boolean(),
  groupRequested: z.boolean(),
  requestAccepted: z.boolean(),
  private: z.boolean(),
});

export const membershipApiSchema = z.object({
  userId: z.string(),
  count: z.number().nullable(),
  groups: z.array(groupMembershipSchema),
});

export type Membership = z.infer<typeof membershipApiSchema>;

export const groupPublicInfoSchema = z.object({
  groupId: z.string(),
  name: z.string(),
  description: z.string(),
  profileText: z.string(),
  avatarAssetId: z.string().nullable(),
  indexPostId: z.string().nullable(),
  privacyPolicyPostId: z.string().nullable(),
  creationTime: z.string(),
});

export type GroupPublicInfo = z.infer<typeof groupPublicInfoSchema>;

const licensePropertySchema = z.object({
  eventId: z.string(),
  key: z.string(),
  startTime: z.string(),
  endTime: z.string(),
  expired: z.boolean(),
  expiredTime: z.string(),
  expiredEventId: z.string().nullable(),
  value: z.union([z.boolean(), z.number()]),
});

export const licenseDataSchema = z.object({
  groupId: z.string(),
  rootGroupId: z.string().optional(), // optional when it comes via useGroupModel
  licenseProperties: z.array(licensePropertySchema),
});

export const accessTypeStringSchema = z.enum([
  "group.view",
  "group.disable",
  "group.destroy",
  "profile.modify",
  "license.modify",
  "access.view",
  "access.modify",
  "members.view",
  "members.add",
  "members.remove",
  "connections.view",
  "connections.add",
  "connections.remove",
  "subgroups.view",
  "subgroups.modify",
  "post.view",
  "post.modify",
  "post.publish",
  "post.destroy",
  "discussion.view",
  "discussion.disable",
  "resource.view",
  "resource.destroy",
  "resource.transfer",
  "resource.modify",
  "resource.translate",
  "questionnaire.lock",
  "questionnaire.publish",
  "responses.view",
  "responses.modify",
  "report.view",
  "log.view",
]);

export type AccessTypeString = z.infer<typeof accessTypeStringSchema>;
const superUserAccessTypeStringSchema = z.enum(["read", "write", "licensing", "responses"]);
export type SuperUserAccessTypeString = z.infer<typeof superUserAccessTypeStringSchema>;

const accessTypeSchema = z.object({
  accessType: accessTypeStringSchema,
  isMember: z.boolean(),
});

export const accessDataSchema = z.object({
  userId: z.string(),
  groupId: z.string(),
  accessCategoryList: z.array(z.string()),
  accessTypeList: z.array(accessTypeSchema),
});

export const superAccessMappings: Record<AccessTypeString, SuperUserAccessTypeString> = {
  "group.view": "read",
  "group.disable": "write",
  "group.destroy": "write",
  "profile.modify": "write",
  "license.modify": "licensing",
  "access.view": "read",
  "access.modify": "write",
  "members.view": "read",
  "members.add": "write",
  "members.remove": "write",
  "connections.view": "read",
  "connections.add": "write",
  "connections.remove": "write",
  "subgroups.view": "read",
  "subgroups.modify": "write",
  "post.view": "read",
  "post.modify": "write",
  "post.publish": "write",
  "post.destroy": "write",
  "discussion.view": "read",
  "discussion.disable": "write",
  "resource.view": "read",
  "resource.destroy": "write",
  "resource.transfer": "write",
  "resource.modify": "write",
  "resource.translate": "write",
  "questionnaire.lock": "write",
  "questionnaire.publish": "write",
  "responses.view": "responses",
  "responses.modify": "responses",
  "report.view": "read",
  "log.view": "read",
};
